import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  History,
  Search,
  Filter,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  User,
  Calendar,
  Activity,
  FileText,
  Phone,
  Settings,
  Shield,
  Eye,
  ArrowLeft,
} from 'lucide-react';
import Header from '../../components/Header';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { apiClient, AuditLog, AuditLogListParams, PaginatedResponse } from '../../services/api';

// Action type labels (matching backend AuditAction enum format: lowercase with dots)
const ACTION_LABELS: Record<string, string> = {
  'user.create': 'Usuario Criado',
  'user.update': 'Usuario Atualizado',
  'user.delete': 'Usuario Removido',
  'user.deactivate': 'Usuario Desativado',
  'user.activate': 'Usuario Ativado',
  'auth.password_change': 'Senha Alterada',
  'auth.login_success': 'Login Sucesso',
  'auth.login_failed': 'Login Falha',
  'campaign.create': 'Campanha Criada',
  'campaign.update': 'Campanha Atualizada',
  'campaign.delete': 'Campanha Removida',
  'campaign.start': 'Campanha Iniciada',
  'campaign.pause': 'Campanha Pausada',
  'campaign.cancel': 'Campanha Cancelada',
  'template.create': 'Template Criado',
  'template.update': 'Template Atualizado',
  'template.delete': 'Template Removido',
  'waba_account.create': 'WABA Criado',
  'waba_account.update': 'WABA Atualizado',
  'waba_account.delete': 'WABA Removido',
  'waba_access.grant': 'Acesso WABA Concedido',
  'waba_access.revoke': 'Acesso WABA Revogado',
  'waba_access.update': 'Acesso WABA Atualizado',
};

// Target type labels (matching backend AuditTargetType enum format: lowercase)
const TARGET_LABELS: Record<string, string> = {
  user: 'Usuario',
  campaign: 'Campanha',
  campaign_template: 'Template de Campanha',
  waba_account: 'Conta WABA',
  waba_access: 'Acesso WABA',
  dispatch: 'Disparo',
  tenant: 'Tenant',
};

// Action icons (matching backend format: lowercase with dots)
const getActionIcon = (action: string) => {
  if (action.startsWith('user.') || action.startsWith('auth.')) return User;
  if (action.startsWith('campaign.')) return FileText;
  if (action.startsWith('template.')) return FileText;
  if (action.startsWith('waba_')) return Phone;
  if (action.startsWith('dispatch.')) return Activity;
  return Settings;
};

// Action colors (matching backend format: lowercase with dots)
const getActionColor = (action: string) => {
  if (action.includes('.create') || action.includes('.grant') || action === 'auth.login_success') {
    return 'text-success bg-success/10';
  }
  if (action.includes('.delete') || action.includes('.revoke') || action === 'auth.login_failed') {
    return 'text-error bg-error/10';
  }
  if (action.includes('.update') || action.includes('password_change')) {
    return 'text-accent-1 bg-accent-1/10';
  }
  return 'text-text-2 bg-bg-3';
};

export default function AuditLogs() {
  const navigate = useNavigate();
  const { accessToken, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // Filters
  const [searchActorId, setSearchActorId] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterTargetType, setFilterTargetType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Detail view
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  // Check if user has permission before loading
  const hasPermission = user?.role === 'super_admin' || user?.role === 'admin';

  useEffect(() => {
    if (hasPermission) {
      loadAuditLogs();
    }
  }, [accessToken, pagination.page, hasPermission]);

  const loadAuditLogs = async () => {
    if (!accessToken || !hasPermission) return;

    try {
      setLoading(true);

      const params: AuditLogListParams = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (searchActorId) params.actorId = searchActorId;
      if (filterAction) params.action = filterAction;
      if (filterTargetType) params.targetType = filterTargetType;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await apiClient.getAuditLogs(params, accessToken);

      if (response.success && response.data) {
        // Backend returns { logs, total, limit, offset } instead of { data, meta }
        const data = response.data as any;
        setLogs(data.logs || data.data || []);

        const total = data.total || data.meta?.total || 0;
        const limit = data.limit || params.limit || 20;
        const offset = data.offset || 0;

        setPagination({
          page: Math.floor(offset / limit) + 1,
          limit: limit,
          total: total,
          totalPages: Math.ceil(total / limit) || 0,
        });
      }
    } catch (error) {
      console.error('Erro ao carregar logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    loadAuditLogs();
  };

  const handleClearFilters = () => {
    setSearchActorId('');
    setFilterAction('');
    setFilterTargetType('');
    setStartDate('');
    setEndDate('');
    setPagination((prev) => ({ ...prev, page: 1 }));
    loadAuditLogs();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatDetails = (details: Record<string, unknown> | undefined) => {
    if (!details) return null;
    return JSON.stringify(details, null, 2);
  };

  // Check permissions (roles are lowercase from backend)
  if (!hasPermission) {
    return (
      <div className="min-h-screen bg-bg-1">
        <Header title="Logs de Auditoria" />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-bg-2 border border-error/30 rounded-lg p-8 text-center">
            <Shield size={48} className="mx-auto text-error mb-4" />
            <h2 className="text-xl font-semibold text-text-1 mb-2">Acesso Negado</h2>
            <p className="text-text-2">
              Voce nao tem permissao para acessar os logs de auditoria.
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-1">
      <Header title="Logs de Auditoria" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center text-text-2 hover:text-text-1 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar ao Dashboard
        </button>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <History size={24} className="text-accent-1" />
            <div>
              <h1 className="text-2xl font-bold text-text-1">Logs de Auditoria</h1>
              <p className="text-text-3 text-sm">Historico de acoes realizadas no sistema</p>
            </div>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              showFilters
                ? 'bg-accent-1 text-white'
                : 'bg-bg-2 border border-bg-3 text-text-2 hover:text-text-1'
            }`}
          >
            <Filter size={18} />
            Filtros
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="bg-bg-2 border border-bg-3 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm text-text-2 mb-1">ID do Ator</label>
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-3" />
                  <input
                    type="text"
                    value={searchActorId}
                    onChange={(e) => setSearchActorId(e.target.value)}
                    placeholder="UUID do usuario"
                    className="w-full pl-9 pr-4 py-2 bg-bg-1 border border-bg-3 rounded-lg text-text-1 text-sm focus:outline-none focus:ring-2 focus:ring-accent-1"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-text-2 mb-1">Acao</label>
                <select
                  value={filterAction}
                  onChange={(e) => setFilterAction(e.target.value)}
                  className="w-full px-4 py-2 bg-bg-1 border border-bg-3 rounded-lg text-text-1 text-sm focus:outline-none focus:ring-2 focus:ring-accent-1"
                >
                  <option value="">Todas as acoes</option>
                  {Object.entries(ACTION_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-text-2 mb-1">Tipo de Alvo</label>
                <select
                  value={filterTargetType}
                  onChange={(e) => setFilterTargetType(e.target.value)}
                  className="w-full px-4 py-2 bg-bg-1 border border-bg-3 rounded-lg text-text-1 text-sm focus:outline-none focus:ring-2 focus:ring-accent-1"
                >
                  <option value="">Todos os tipos</option>
                  {Object.entries(TARGET_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm text-text-2 mb-1">De</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 bg-bg-1 border border-bg-3 rounded-lg text-text-1 text-sm focus:outline-none focus:ring-2 focus:ring-accent-1"
                  />
                </div>
                <div>
                  <label className="block text-sm text-text-2 mb-1">Ate</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 bg-bg-1 border border-bg-3 rounded-lg text-text-1 text-sm focus:outline-none focus:ring-2 focus:ring-accent-1"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleSearch}
                className="flex items-center gap-2 px-4 py-2 bg-accent-1 hover:bg-accent-2 text-white rounded-lg transition-colors"
              >
                <Search size={16} />
                Buscar
              </button>
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 bg-bg-3 text-text-2 hover:text-text-1 rounded-lg transition-colors"
              >
                Limpar
              </button>
            </div>
          </div>
        )}

        {/* Logs Table */}
        <div className="bg-bg-2 border border-bg-3 rounded-lg overflow-hidden">
          {loading ? (
            <LoadingSpinner size="lg" />
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <History size={48} className="mx-auto text-text-3 mb-3" />
              <p className="text-text-2">Nenhum log encontrado</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-bg-3">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-2 uppercase">
                    Data/Hora
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-2 uppercase">
                    Ator
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-2 uppercase">
                    Acao
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-2 uppercase">
                    Alvo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-2 uppercase">
                    IP
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-text-2 uppercase">
                    Detalhes
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-bg-3">
                {logs.map((log) => {
                  const ActionIcon = getActionIcon(log.action);
                  const actionColor = getActionColor(log.action);

                  return (
                    <tr key={log.id} className="hover:bg-bg-3/50">
                      <td className="px-4 py-3 text-sm text-text-1">
                        <div className="flex items-center gap-2">
                          <Calendar size={14} className="text-text-3" />
                          {formatDate(log.createdAt)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-text-1">{log.actorName || log.actor?.name || 'Sistema'}</div>
                        <div className="text-xs text-text-3 font-mono">
                          {log.actorId ? `${log.actorId.slice(0, 8)}...` : '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${actionColor}`}
                        >
                          <ActionIcon size={12} />
                          {ACTION_LABELS[log.action] || log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-text-2">
                          {TARGET_LABELS[log.targetType] || log.targetType}
                        </div>
                        {log.targetId && (
                          <div className="text-xs text-text-3 font-mono">
                            {log.targetId.slice(0, 8)}...
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-3 font-mono">
                        {log.ipAddress || '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {log.details && (
                          <button
                            onClick={() => setSelectedLog(log)}
                            className="p-1.5 text-text-3 hover:text-accent-1 transition-colors"
                            title="Ver detalhes"
                          >
                            <Eye size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!loading && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-text-3">
              Mostrando {(pagination.page - 1) * pagination.limit + 1} a{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}{' '}
              registros
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="p-2 bg-bg-2 border border-bg-3 rounded-lg text-text-2 hover:text-text-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={18} />
              </button>
              <span className="flex items-center px-4 text-sm text-text-2">
                Pagina {pagination.page} de {pagination.totalPages}
              </span>
              <button
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.totalPages}
                className="p-2 bg-bg-2 border border-bg-3 rounded-lg text-text-2 hover:text-text-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Details Modal */}
        {selectedLog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-bg-1 border border-bg-3 rounded-lg w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden shadow-xl">
              <div className="flex items-center justify-between p-4 border-b border-bg-3">
                <h3 className="text-lg font-semibold text-text-1">Detalhes do Log</h3>
                <button
                  onClick={() => setSelectedLog(null)}
                  className="text-text-3 hover:text-text-1"
                >
                  &times;
                </button>
              </div>
              <div className="p-4 overflow-y-auto max-h-[60vh]">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-text-3 mb-1">Acao</p>
                    <p className="text-sm text-text-1">
                      {ACTION_LABELS[selectedLog.action] || selectedLog.action}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-text-3 mb-1">Data/Hora</p>
                    <p className="text-sm text-text-1">{formatDate(selectedLog.createdAt)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-text-3 mb-1">Ator</p>
                    <p className="text-sm text-text-1">{selectedLog.actorName || selectedLog.actor?.name || 'Sistema'}</p>
                    <p className="text-xs text-text-3 font-mono">{selectedLog.actorId || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-text-3 mb-1">Alvo</p>
                    <p className="text-sm text-text-1">
                      {TARGET_LABELS[selectedLog.targetType] || selectedLog.targetType}
                    </p>
                    {selectedLog.targetId && (
                      <p className="text-xs text-text-3 font-mono">{selectedLog.targetId}</p>
                    )}
                  </div>
                  {selectedLog.ipAddress && (
                    <div>
                      <p className="text-xs text-text-3 mb-1">IP</p>
                      <p className="text-sm text-text-1 font-mono">{selectedLog.ipAddress}</p>
                    </div>
                  )}
                </div>

                {selectedLog.details && (
                  <div>
                    <p className="text-xs text-text-3 mb-2">Detalhes</p>
                    <pre className="bg-bg-1 border border-bg-3 rounded-lg p-3 text-xs text-text-2 overflow-x-auto">
                      {formatDetails(selectedLog.details)}
                    </pre>
                  </div>
                )}
              </div>
              <div className="flex justify-end p-4 border-t border-bg-3">
                <button
                  onClick={() => setSelectedLog(null)}
                  className="px-4 py-2 bg-bg-3 text-text-2 hover:text-text-1 rounded-lg transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
